import { faCircleXmark } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import s from './TableComparisonView.module.css'
import Image from 'next/image'
import { IDiamond } from 'types/diamond.types'
import { diamondShapeImg } from '@utils/diamondShapes'
import Link from 'next/link'
import { formatUSDollar } from '@utils/format-price'
import { formatPercent } from '@utils/format-percent'

const TableComparisonView: React.FC<{
  itemsToCompare: Partial<IDiamond>[]
  setItemsToCompare: (arg: Partial<IDiamond>[]) => void
  setActiveRadio: Function
}> = ({ itemsToCompare, setItemsToCompare, setActiveRadio }) => {
  const shapes = {
    RB: 'Round',
    PR: 'Princess',
    CUS: 'Cushion',
    EC: 'Emerald',
    AS: 'Asscher',
    SQR: 'Square Radiant',
    OV: 'Oval',
    RAD: 'Radiant',
    MQ: 'Marquise',
    PS: 'Pear',
    HS: 'Heart',
    TR: 'Trillian',
  }
  const polsymm = {
    0: 'Poor',
    1: 'Fair',
    2: 'Good',
    3: 'Very Good',
    4: 'Excellent',
    5: 'Ideal',
  }

  const removeDiamond = (diamond: Partial<IDiamond>) => {
    setItemsToCompare(itemsToCompare.filter((item) => item.id !== diamond.id))
    itemsToCompare.length === 1 ? setActiveRadio(2) : ''
  }

  return (
    <>
      <div className={s.listGroup}>
        {itemsToCompare &&
          itemsToCompare.map((diamond) => {
            return (
              <div key={diamond.id} className={s.item}>
                <div className={s.border}>
                  <div className="h-[25px] relative mb-px">
                    <div className={s.close}>
                      <button onClick={() => removeDiamond(diamond)}>
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          className={s.iconClose}
                        />
                      </button>
                    </div>
                  </div>
                  <Link href={`/diamond/${diamond.id}`}>
                    <a className="block">
                      <div className={s.littleItem}>
                        <div className="text-center">
                          <div className={s.main}>
                            <div className="h-full w-full hover:cursor-pointer">
                              <Image
                                src={
                                  diamondShapeImg[
                                    diamond.shape?.toUpperCase() as keyof typeof diamondShapeImg
                                  ]
                                }
                                alt="diamond image"
                                width={250}
                                height={250}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="uppercase relative">
                          <div className="py-[5px] px-[10px]">
                            <div className="text-center">
                              <span className="text-xs align-text-bottom">
                                {diamond.weight} CARAT {diamond.shape}{' '}
                                {diamond.color} {diamond.clarity}
                              </span>
                            </div>
                            <div className={s.props}>
                              <table className="cursor-pointer">
                                <tbody>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Shape</td>
                                    <td className={s.propValue}>
                                      <span>
                                        {
                                          shapes[
                                            diamond.shape as keyof typeof shapes
                                          ]
                                        }
                                      </span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Carat</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.weight}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Color</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.color}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Clarity</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.clarity}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Cut</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.cut}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>
                                      Fluorescence
                                    </td>
                                    <td className={s.propValue}>
                                      <span>{diamond.fluor}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>Lab</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.lab}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>polish</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.polish || '-'}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>symmetry</td>
                                    <td className={s.propValue}>
                                      <span>{diamond.symmetry || '-'}</span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>depth</td>
                                    <td className={s.propValue}>
                                      <span>
                                        {formatPercent(diamond.depth, 1) || '-'}
                                      </span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>table</td>
                                    <td className={s.propValue}>
                                      <span>
                                        {formatPercent(diamond.tablep, 1) ||
                                          '-'}
                                      </span>
                                    </td>
                                  </tr>
                                  <tr className={s.tr}>
                                    <td className={s.propTitle}>ratio</td>
                                    <td className={s.propValue}>
                                      <span>
                                        {diamond.ratio
                                          ? parseFloat(diamond.ratio).toFixed(2)
                                          : '-'}
                                      </span>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>{' '}
                          </div>
                        </div>
                        <div className={s.productPrice}>
                          {formatUSDollar(Math.round(Number(diamond.price)))}
                        </div>
                      </div>
                    </a>
                  </Link>
                </div>
              </div>
            )
          })}
      </div>
    </>
  )
}

export default TableComparisonView
